import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useParams} from "react-router";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClose, faSearch} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Contexts
import {FacilityNavContext} from "../contexts/facilitynav.js";
import {AuthContext} from "../contexts/auth.js";
import {NavContext} from "../contexts/nav.js";
import {useSocket} from "../contexts/socket.js";

// Hooks
import useApi from "../hooks/useApi.js";
import useMountedState from "../hooks/useMountedState.js";

// Utils
import {COMPONENT, FIELD, FIELD_TYPES, getAncestors, GROUP} from "../utils/builder.js";
import {toTitleCase} from "../utils/helpers.js";

// Components
import Badge from "../components/Badge.js";
import RenderChecksheet from "./checksheet-builder/RenderChecksheet.js";
import FacilityPageHeader from "./general/FacilityPageHeader.js";
import Modal from "../components/Modal.js";
import Select from "../components/Select.js";

// Style
import {flex, z} from "../style/components/mixins.js";
import {voice} from "../style/components/typography.js";
import {shake} from "../style/components/animations.js";
import {pad, border, radius, navHeight, shadow, colors} from "../style/components/variables.js";
import {
  Page,
  Title,
  Inline,
  Abbr,
  NotLoaded,
  Loader,
  Pill,
  Button,
  Text,
  HeadingMedium,
  Input,
  Label,
  StickyWrapper,
  SearchWrapper,
  SearchIcon,
  Search,
  Small,
  scrollbar
} from "../style/components/general.js";

// Socket Constants
import Room from "./general/Room.js";

const LISTEN_NOTIFY_UPDATE_CHECKSHEET_BUILDER = "notify:update_checksheet_builder";

const DEFAULT_SIMULATED_STATE = {
  name: "DEFAULT",
  id: "0"
};

/** Merge default checksheet builder with a state variant (builder or builderOverrides from API). */
const resolvePreviewBuilder = (checksheet, simulatedStateId) => {
  const defaultBuilder = checksheet?.builder;
  if (!defaultBuilder || simulatedStateId === DEFAULT_SIMULATED_STATE.id) return defaultBuilder;

  const {state} = checksheet;
  if (!state || `${state.facilityStateId}` !== `${simulatedStateId}`) return defaultBuilder;

  if (state.builder) return state.builder;
  if (!state.builderOverrides?.byId) return defaultBuilder;

  const byId = Object.fromEntries(
    Object.entries(defaultBuilder.byId).map(([name, attrs]) => [name, {...attrs}])
  );
  let {allIds} = defaultBuilder;

  Object.entries(state.builderOverrides.byId).forEach(([name, override]) => {
    if (override.deleted) {
      if (byId[name]) byId[name] = {...byId[name], deleted: true};
      return;
    }

    const existing = byId[name];
    if (existing) {
      byId[name] = {
        ...existing,
        ...override,
        children: existing.children?.length ? existing.children : override.children ?? []
      };
    } else {
      byId[name] = {...override, children: override.children ?? []};
      if (!override.parentName && !allIds.includes(name)) allIds = [...allIds, name];
    }
  });

  if (state.builderOverrides.allIds?.length) {
    state.builderOverrides.allIds.forEach(id => {
      if (!allIds.includes(id) && byId[id] && !byId[id].parentName) allIds = [...allIds, id];
    });
  }

  return {allIds, byId};
};

/** Mark fields that exist on both the preview variant and the other state variant. */
const annotateLinkedStateFields = (builder, otherBuilder) => {
  if (!builder?.byId || !otherBuilder?.byId) return builder;

  const byId = Object.fromEntries(
    Object.entries(builder.byId).map(([name, attrs]) => {
      if (
        attrs.element === FIELD &&
        !attrs.deleted &&
        otherBuilder.byId[name] &&
        !otherBuilder.byId[name].deleted
      ) {
        return [name, {...attrs, linkedAcrossStates: true}];
      }
      return [name, attrs];
    })
  );

  return {...builder, byId};
};

/** Resolve frequency for preview when simulating a facility state variant. */
const resolvePreviewFrequency = (checksheet, simulatedStateId) => {
  if (!checksheet?.frequency || simulatedStateId === DEFAULT_SIMULATED_STATE.id) {
    return checksheet?.frequency;
  }

  const {state} = checksheet;
  if (!state || `${state.facilityStateId}` !== `${simulatedStateId}`) {
    return checksheet.frequency;
  }

  return state.frequency || checksheet.frequency;
};

const ChecksheetPreview = () => {
  const isMounted = useMountedState();

  const {slug} = useParams();

  const socket = useSocket();

  const {currentUser} = useContext(AuthContext);
  const {allStates} = useContext(NavContext);

  const {edit} = useParams();

  const {facility, setFacility} = useContext(FacilityNavContext);

  const {api: apiFacility} = useApi("facilities");
  const {api: apiChecksheet, loading} = useApi("checksheets");
  const {api: apiFieldTags} = useApi("field-tags", {suppress: {success: true, error: true}});

  const [active, setActive] = useState(null);
  const [interactive, setInteractive] = useState(false);
  const [checksheet, setChecksheet] = useState();
  const [hasChanges, setHasChanges] = useState(false);
  const [simulatedDate, setSimulatedDate] = useState(null);
  const [simulatedFacilityStateId, setSimulatedFacilityStateId] = useState(
    DEFAULT_SIMULATED_STATE.id
  );
  // Filter
  const [open, setOpen] = useState(false);
  const [fieldTags, setFieldTags] = useState([]);
  const [targetTypes, setTargetTypes] = useState([]);
  const [targetTags, setTargetTags] = useState([]);
  const [targetElements, setTargetElements] = useState([]);
  const [filters, setFilters] = useState([]);
  const [filterResults, setFilterResults] = useState();
  // Modals
  const [showModalModified, setShowModalModified] = useState(false);

  const search = useRef();
  const suggestion = useRef();

  const readOnly = useMemo(() => !interactive, [interactive]);

  const getFacility = useCallback(
    id =>
      apiFacility.callGet(id).then(({status, data}) => {
        if (status === 200 && data) setFacility(data);
      }),
    [apiFacility, setFacility]
  );

  const getChecksheet = useCallback(
    id =>
      apiChecksheet.callGet(id, {withAllStates: true}).then(({status, data}) => {
        if (status === 200 && data) {
          setChecksheet(data);

          if (!facility || facility.id !== data.facility.id) getFacility(data.facility.id);
        }
      }),
    [apiChecksheet, facility, getFacility]
  );

  const facilityStates = useMemo(() => {
    if (!allStates || Object.keys(allStates).length === 0) return null;
    return Object.entries(allStates).map(([name, {id, color}]) => ({
      name: name.toUpperCase(),
      id: `${id}`,
      color
    }));
  }, [allStates]);

  const previewStateSelectOptions = useMemo(() => {
    const rows = [{label: DEFAULT_SIMULATED_STATE.name, name: DEFAULT_SIMULATED_STATE.id}];
    if (!facilityStates?.length || !checksheet?.state) return rows;
    const state = facilityStates.find(({id}) => id === `${checksheet.state.facilityStateId}`);
    if (!state) return rows;
    rows.push({label: state.name.toUpperCase(), name: `${state.id}`, color: state.color});
    return rows;
  }, [facilityStates, checksheet]);

  const currentSimulatedState = useMemo(
    () => facilityStates?.find(({id}) => id === simulatedFacilityStateId) || null,
    [facilityStates, simulatedFacilityStateId]
  );

  const handlePreviewStateSelect = useCallback(selection => {
    setSimulatedFacilityStateId(`${selection.name}`);
  }, []);

  const previewBuilder = useMemo(
    () => resolvePreviewBuilder(checksheet, simulatedFacilityStateId),
    [checksheet, simulatedFacilityStateId]
  );

  const otherPreviewBuilder = useMemo(() => {
    if (!checksheet?.state) return null;
    const stateFacilityId = `${checksheet.state.facilityStateId}`;
    if (simulatedFacilityStateId === DEFAULT_SIMULATED_STATE.id) {
      return resolvePreviewBuilder(checksheet, stateFacilityId);
    }
    return checksheet.builder;
  }, [checksheet, simulatedFacilityStateId]);

  const annotatedPreviewBuilder = useMemo(
    () => annotateLinkedStateFields(previewBuilder, otherPreviewBuilder),
    [previewBuilder, otherPreviewBuilder]
  );

  const previewFrequency = useMemo(
    () => resolvePreviewFrequency(checksheet, simulatedFacilityStateId),
    [checksheet, simulatedFacilityStateId]
  );

  const previewTask = useMemo(() => {
    if (!checksheet) return null;
    return {
      ...checksheet,
      name: null,
      frequency: previewFrequency,
      builder: annotatedPreviewBuilder || previewBuilder || checksheet.builder
    };
  }, [checksheet, annotatedPreviewBuilder, previewBuilder, previewFrequency]);

  useEffect(() => {
    if (isMounted() && !checksheet && edit) getChecksheet(edit);
  }, [isMounted, checksheet, edit, getChecksheet]);

  useEffect(() => {
    if (isMounted())
      apiFieldTags.callGet().then(({status, data}) => {
        if (status === 200 && data) setFieldTags(data.map(({name}) => name.toLowerCase()));
      });
  }, [isMounted, apiFieldTags]);

  // Socket Management
  const notifyUpdate = useCallback(() => setHasChanges(true), []);

  useEffect(() => {
    socket.on(LISTEN_NOTIFY_UPDATE_CHECKSHEET_BUILDER, notifyUpdate);

    return () => {
      // unbind all event handlers used in this component
      socket.off(LISTEN_NOTIFY_UPDATE_CHECKSHEET_BUILDER, notifyUpdate);
    };
  }, [isMounted, currentUser, socket, slug, notifyUpdate]);

  const getElementMatches = (target, ids, byId, result = []) => {
    ids.map(id => {
      if (byId[id]) {
        const {element, type, name, label, parentName} = byId[id];

        const ancestors = getAncestors(parentName, byId);
        const value = ancestors ? `${ancestors} ${label}` : label;

        if (name?.includes(target)) {
          // case for rainfall
          if (element === "field" && type === "weather")
            result.push({
              element,
              name: `${name}_rainfall`,
              label: `${toTitleCase(value)} Rainfall`
            });

          result.push({
            element,
            name,
            label: toTitleCase(value)
          });
        }
      }

      if (byId[id] && byId[id].children.length > 0)
        getElementMatches(target, byId[id].children, byId, result);
    });

    return result;
  };

  const handleSearch = ({target}) => {
    if (target?.value) {
      const types = FIELD_TYPES.filter(type =>
        type.label.replace(/ /g, "").toLowerCase().includes(target.value.toLowerCase())
      );
      setTargetTypes(types);

      const filtered = fieldTags.filter(name =>
        name.toLowerCase().includes(target.value.toLowerCase())
      );
      setTargetTags(filtered);

      const elementMatches = getElementMatches(
        target.value.toLowerCase(),
        checksheet.builder.allIds,
        checksheet.builder.byId
      );
      setTargetElements(elementMatches.sort((a, b) => a.label - b.label));
    }
  };

  useEffect(() => {
    const handleOutsideClick = e =>
      open &&
      suggestion &&
      suggestion.current !== null &&
      !suggestion.current.contains(e.target) &&
      !suggestion.current.contains(e.target.nextSibling) &&
      setOpen(false);

    if (suggestion) document.addEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  return (
    <Page hasMenu>
      <Room name="checksheet-preview" active={active} setActive={setActive} />

      <Inline fullWidth>
        {facility?.name && <FacilityPageHeader facility={facility} path="/schedule" disabled />}
        {hasChanges && (
          <Changes
            type="button"
            title="Events have changed since your last page load"
            onClick={() => setShowModalModified(true)}>
            Load new changes!
          </Changes>
        )}
        <Button type="button" onClick={() => setInteractive(prev => !prev)}>
          {interactive ? "Preview" : "Interactive"}
        </Button>
      </Inline>

      {checksheet && !loading ? (
        <Wrapper data-testid="checksheet.preview">
          <Menu>
            <Inline>
              <ChecksheetName>
                <Abbr title={checksheet.name}>{checksheet.name}</Abbr>
              </ChecksheetName>
              <PreviewHeader>
                {previewStateSelectOptions.length > 1 && (
                  <PreviewStateSelect data-testid="checksheet.previewStateSelect">
                    <Select
                      options={previewStateSelectOptions}
                      selection={simulatedFacilityStateId}
                      selectionLabel={currentSimulatedState?.name || DEFAULT_SIMULATED_STATE.name}
                      setSelection={handlePreviewStateSelect}
                      pills
                      compact
                      color={
                        currentSimulatedState?.color ? `#${currentSimulatedState.color}` : undefined
                      }
                      inverted={false}
                      menuAlignEnd
                    />
                  </PreviewStateSelect>
                )}
                {previewFrequency && <Frequency>{previewFrequency.name}</Frequency>}
              </PreviewHeader>
            </Inline>

            <Options>
              {interactive && (
                <>
                  <SearchWrapper>
                    <SearchIcon>
                      <FontAwesomeIcon icon={faSearch} />
                    </SearchIcon>
                    <Search
                      ref={search}
                      name="search"
                      type="text"
                      placeholder="Find..."
                      onChange={handleSearch}
                      onFocus={() => setOpen(true)}
                    />
                    {filters.length > 0 && (
                      <Badge
                        count={filters.length}
                        title="Active filters"
                        offsetY="25px"
                        offsetX="-10px"
                        color={colors.heroGreen}
                      />
                    )}
                    <Suggestions
                      ref={suggestion}
                      open={
                        (filterResults?.length > 0 || search.current?.value?.length > 0) && open
                      }>
                      <Filters style={{flexWrap: "wrap"}}>
                        {filters?.map(filter => (
                          <Filter
                            key={filter}
                            type="button"
                            onClick={() => {
                              const [fType, fName] = filter.split(": ");
                              setFilterResults(prev => {
                                const newResults = prev?.filter(id => {
                                  if (fType === "TYPE")
                                    return checksheet.builder.byId[id].type !== fName.toLowerCase();
                                  if (fType === "TAG")
                                    return (
                                      checksheet.builder.byId[id]?.tag?.toUpperCase() !==
                                      fName.toUpperCase()
                                    );
                                  return true;
                                });
                                if (filters.length > 1) return newResults;
                                return undefined;
                              });
                              setFilters(prev => prev.filter(f => f !== filter));
                            }}>
                            <Pill quiet>
                              {filter}&nbsp;
                              <Icon icon={faClose} />
                            </Pill>
                          </Filter>
                        ))}
                      </Filters>
                      {(targetTypes?.length > 0 || targetTags?.length > 0) && (
                        <>
                          <Small>Filter</Small>
                          <hr />
                          {targetTypes?.length > 0 && (
                            <>
                              <Text quiet>Types:</Text>
                              {targetTypes.map(({label, value}) => (
                                <Suggestion
                                  key={label}
                                  onClick={() => {
                                    search.current.value = label.toUpperCase();
                                    handleSearch({target: {value: label}});
                                    const typeMatches = Object.fromEntries(
                                      Object.entries(checksheet.builder.byId).filter(
                                        ([, {type: t}]) => t && value?.includes(t.toLowerCase())
                                      )
                                    );
                                    setFilterResults(prev =>
                                      prev
                                        ? [...prev, ...Object.keys(typeMatches)]
                                        : Object.keys(typeMatches)
                                    );

                                    // reset
                                    setTargetTypes(undefined);
                                    setTargetTags(undefined);
                                    setTargetElements(undefined);
                                    setFilters(prev => [...prev, `TYPE: ${label.toUpperCase()}`]);
                                    setOpen(false);
                                    search.current.value = "";
                                  }}>
                                  {label.toUpperCase()}
                                </Suggestion>
                              ))}
                            </>
                          )}
                          {targetTags?.length > 0 && (
                            <>
                              <Text quiet>Tags:</Text>
                              {targetTags.map(tag => (
                                <Suggestion
                                  key={tag}
                                  onClick={() => {
                                    search.current.value = tag.toUpperCase();
                                    handleSearch({target: {value: tag}});
                                    const tagMatches = Object.fromEntries(
                                      Object.entries(checksheet.builder.byId).filter(
                                        ([, {tag: t}]) => t && targetTags?.includes(t.toLowerCase())
                                      )
                                    );
                                    setFilterResults(prev =>
                                      prev
                                        ? [...prev, ...Object.keys(tagMatches)]
                                        : Object.keys(tagMatches)
                                    );

                                    // reset
                                    setTargetTypes(undefined);
                                    setTargetTags(undefined);
                                    setTargetElements(undefined);
                                    setFilters(prev => [...prev, `TAG: ${tag.toUpperCase()}`]);
                                    setOpen(false);
                                    search.current.value = "";
                                  }}>
                                  {tag.toUpperCase()}
                                </Suggestion>
                              ))}
                            </>
                          )}
                        </>
                      )}
                      {targetElements?.length > 0 && (
                        <>
                          <hr />
                          <Small>Find Element</Small>
                          <hr />
                          {targetElements.map(({element, name, label}) => (
                            <Suggestion
                              key={name}
                              element={element}
                              onClick={() => {
                                handleSearch({target: {value: label}});
                                document
                                  .querySelectorAll(`[data-testid='${element}-${name}']`)[0]
                                  .scrollIntoView({
                                    behavior: "smooth",
                                    block: "center"
                                  });

                                // reset
                                setTargetTypes(undefined);
                                setTargetTags(undefined);
                                setTargetElements(undefined);
                                setOpen(false);
                                search.current.value = "";
                              }}>
                              {label.toUpperCase()}
                            </Suggestion>
                          ))}
                        </>
                      )}
                      {targetTypes?.length === 0 &&
                        targetTags?.length === 0 &&
                        targetElements?.length === 0 && <Text>No matches found.</Text>}
                    </Suggestions>
                  </SearchWrapper>
                  <DateInputLabel bold htmlFor="simulatedDate">
                    Simulated Date
                    <Input
                      type="date"
                      onChange={e => setSimulatedDate(e.target.value)}
                      name="simulatedDate"
                      defaultValue={dayjs().format("YYYY-MM-DD")}
                    />
                  </DateInputLabel>
                </>
              )}
            </Options>
          </Menu>
          <RenderChecksheet
            task={previewTask}
            hideMeta
            hideCompleted
            readOnly={readOnly}
            simulatedDate={simulatedDate}
            filterResults={filterResults}
            freezeComponentState={readOnly}
            simulating
          />
        </Wrapper>
      ) : (
        <NotLoaded>
          <Loader />
        </NotLoaded>
      )}

      {showModalModified && (
        <Modal visible={showModalModified} setVisible={setShowModalModified}>
          <HeadingMedium>Missing Changes</HeadingMedium>
          <Text>This checksheet has been changed since you last viewed it.</Text>
          <br />
          <Button
            type="button"
            onClick={() => {
              setShowModalModified(false);
              setHasChanges(false);
              getChecksheet(edit);
            }}>
            Update
          </Button>
        </Modal>
      )}
    </Page>
  );
};

// Style Overrides
const Wrapper = styled.article`
  ${flex("row", "wrap", "start", "start")};
  margin: ${pad * 2}px 0;
  width: 100%;
  padding: ${pad}px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
`;

const Menu = styled(StickyWrapper)`
  ${flex("row", "wrap", "space-between", "end")};
  width: 100%;
  top: ${navHeight}px;
  background: ${({theme}) => theme.tertiary};
  box-shadow: 0 5px 15px ${({theme}) => theme.tertiary};
  z-index: ${z("top")};
`;

const ChecksheetName = styled(Title)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const PreviewHeader = styled(Inline)`
  flex: 0 0 auto;
  flex-shrink: 0;
  align-items: end;
  gap: ${pad}px;
`;

const PreviewStateSelect = styled.div`
  flex: 0 0 auto;
`;

const Frequency = styled(Pill)`
  font-weight: bold;
  padding: 7px ${pad / 1.5}px;
  white-space: nowrap;
`;

const Changes = styled.button`
  ${voice.quiet};
  padding: ${pad}px;
  position: absolute;
  top: 33px;
  bottom: 0;
  animation: ${shake()} 5s linear 2s infinite;

  &:hover {
    text-decoration: underline;
    animation-play-state: paused;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  fill: ${({theme}) => theme.tertiary};
`;

const Filters = styled(Inline)`
  flex-wrap: nowrap;
  margin-bottom: ${pad}px;
`;

const Filter = styled.button``;

const Options = styled(Inline)`
  align-items: end;
`;

const Suggestions = styled.div`
  ${scrollbar};
  position: absolute;
  top: 100%;
  left: 0;
  padding: ${pad}px;
  margin-top: ${pad / 2}px;
  width: 300px;
  max-height: 300px;
  border-radius: ${radius};
  border: ${border} solid ${({theme}) => theme.secondary};
  background: ${({theme}) => theme.tertiary};
  box-shadow: ${shadow};
  overflow-y: auto;

  display: ${({open}) => (open ? "block" : "none")};
`;

const Suggestion = styled.button`
  ${voice.small};
  text-align: left;
  margin-top: ${pad / 2}px;
  color: ${({element, theme}) => {
    if (element && element === GROUP) return theme.group;
    if (element && element === COMPONENT) return theme.component;
    return theme.secondary;
  }};

  &:hover {
    font-weight: bold;
  }
`;

const DateInputLabel = styled(Label)`
  ${voice.quiet};
  display: flex;
  flex-direction: column;
  width: min-content;
`;

export default ChecksheetPreview;
